import type { BrandingConfig, ThemeTokens } from './tokens';
import {
    BRANDING_STORAGE_KEY,
    DEFAULT_BRANDING,
    DEFAULT_THEME_TOKENS,
} from './tokens';
import { buildDerivedTokens } from './colorUtils';

export const applyThemeTokens = (tokens: ThemeTokens) => {
    if (typeof document === 'undefined') {
        return;
    }

    const derived = buildDerivedTokens(tokens);
    const root = document.documentElement;

    root.style.setProperty('--primary', tokens.primary);
    root.style.setProperty('--secondary', tokens.secondary);
    root.style.setProperty('--accent', tokens.accent);
    root.style.setProperty('--background', tokens.background);
    root.style.setProperty('--text', tokens.text);
    root.style.setProperty('--text-secondary', tokens.textSecondary);
    root.style.setProperty('--bg-light', derived.bgLight);
    root.style.setProperty('--bg-light25', derived.bgLight25);
    root.style.setProperty('--primary-light', derived.primaryLight);
    root.style.setProperty('--primary-light25', derived.primaryLight25);
    root.style.setProperty('--primary-light-dark', derived.primaryLightDark);
    root.style.setProperty('--secondary-light', derived.secondaryLight);
    root.style.setProperty('--surface', derived.surface);
    root.style.setProperty('--radius-sm', `${derived.radii.sm}px`);
    root.style.setProperty('--radius-md', `${derived.radii.md}px`);
    root.style.setProperty('--radius-lg', `${derived.radii.lg}px`);
    root.style.setProperty('--radius-xl', `${derived.radii.xl}px`);
    root.style.setProperty('--radius-pill', `${derived.radii.pill}px`);
    root.style.setProperty('--theme-shape', tokens.shape);
    root.dataset.themeShape = tokens.shape;
};

export const loadBrandingConfig = (): BrandingConfig => {
    if (typeof localStorage === 'undefined') {
        return DEFAULT_BRANDING;
    }

    try {
        const raw = localStorage.getItem(BRANDING_STORAGE_KEY);
        if (!raw) {
            return DEFAULT_BRANDING;
        }

        const parsed = JSON.parse(raw) as Partial<BrandingConfig>;
        return {
            tokens: {
                ...DEFAULT_THEME_TOKENS,
                ...(parsed.tokens || {}),
            },
            brandImageUrl: parsed.brandImageUrl ?? null,
        };
    } catch {
        return DEFAULT_BRANDING;
    }
};

export const saveBrandingConfig = (config: BrandingConfig) => {
    if (typeof localStorage === 'undefined') {
        return;
    }

    localStorage.setItem(BRANDING_STORAGE_KEY, JSON.stringify(config));
};

export const hydrateThemeFromStorage = () => {
    const config = loadBrandingConfig();
    applyThemeTokens(config.tokens);
    return config;
};

export const resetBrandingConfig = (): BrandingConfig => {
    if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(BRANDING_STORAGE_KEY);
    }
    applyThemeTokens(DEFAULT_THEME_TOKENS);
    return DEFAULT_BRANDING;
};
